import type { CatItem } from './batch';
import { fetchMoreGallery, fetchMyLikesOnly } from './api';
import { fetchLikeCounts, getLikeInfo, markLiked, onLikeChange, resetLikeCache } from './likes';
import { state, els, galleryEpoch, bumpGalleryEpoch } from './state';

const itemTemplate = document.createElement('template');
{
  const item = document.createElement('div');
  item.className = 'item';
  const pic = document.createElement('div');
  pic.className = 'pic';
  const img = document.createElement('img');
  img.alt = '';
  img.decoding = 'async';
  pic.appendChild(img);
  const meta = document.createElement('div');
  meta.className = 'meta';
  const span = document.createElement('span');
  meta.appendChild(span);
  item.append(pic, meta);
  itemTemplate.content.appendChild(item);
}

let scrollLoadFn: ((gen: number) => void) | null = null;
let scrollCooldown = false;

function nearSentinel(): boolean {
  const r = els.sentinel.getBoundingClientRect();
  return r.top <= window.innerHeight + 250;
}

function stale(gen: number, filter?: string): boolean {
  if (gen !== galleryEpoch) return true;
  if (filter !== undefined && state.filter !== filter) return true;
  return false;
}

export function checkScrollLoad(): void {
  if (!scrollLoadFn || state.loading || state.ended || scrollCooldown || !nearSentinel()) return;
  scrollLoadFn(galleryEpoch);
}

function makeItem(c: CatItem, i: number, open: (index: number) => void): HTMLElement {
  const el = itemTemplate.content.firstElementChild!.cloneNode(true) as HTMLElement;
  el.dataset.i = String(i);
  const img = el.querySelector('img') as HTMLImageElement;
  img.src = c.url;
  img.alt = `кот ${c.id}`;
  if (i < 8) {
    img.loading = 'eager';
    if (i < 4) img.fetchPriority = 'high';
  } else {
    img.loading = 'lazy';
  }
  const meta = el.querySelector('.meta span') as HTMLElement;
  meta.dataset.url = c.url;
  meta.dataset.id = String(c.id);
  meta.textContent = `#${c.id}`;
  el.onclick = () => open(i);
  return el;
}

function rebuildGrid(open: (index: number) => void): void {
  els.grid.innerHTML = '';
  state.allCats.forEach((c, i) => els.grid.appendChild(makeItem(c, i, open)));
  els.count.textContent = String(state.allCats.length);
}

function removeCatsByUrls(urls: ReadonlySet<string>, open: (index: number) => void): void {
  if (urls.size === 0) return;
  state.allCats = state.allCats.filter(c => !urls.has(c.url));
  rebuildGrid(open);
}

function updateMetaLike(url: string): void {
  const info = getLikeInfo(url);
  const likeText = info?.liked ? ' · ♥' : '';
  document.querySelectorAll<HTMLElement>('.meta span[data-url]').forEach(el => {
    if (el.dataset.url !== url) {
      return;
    }
    el.textContent = `#${el.dataset.id ?? ''}${likeText}`;
  });
}

onLikeChange((url, info) => {
  updateMetaLike(url);
});

function verifyLikedBatch(cats: CatItem[], open: (index: number) => void, gen: number): void {
  for (const c of cats) {
    markLiked(c.url, 1);
    updateMetaLike(c.url);
  }
  void fetchLikeCounts(cats.map(c => c.url)).then(() => {
    if (stale(gen, 'liked')) return;
    const drop = new Set<string>();
    for (const c of cats) {
      const info = getLikeInfo(c.url);
      if (info?.liked) {
        updateMetaLike(c.url);
      } else {
        drop.add(c.url);
      }
    }
    if (drop.size > 0) {
      removeCatsByUrls(drop, open);
    }
  });
}

export function appendCats(
  cats: CatItem[],
  open: (index: number) => void,
  gen: number = galleryEpoch,
): void {
  if (stale(gen)) return;

  const start = state.allCats.length;
  state.allCats.push(...cats);
  const frag = document.createDocumentFragment();
  cats.forEach((c, j) => frag.appendChild(makeItem(c, start + j, open)));
  els.grid.appendChild(frag);
  els.count.textContent = String(state.allCats.length);

  if (state.filter === 'liked') {
    verifyLikedBatch(cats, open, gen);
    scrollCooldown = true;
    window.setTimeout(() => {
      scrollCooldown = false;
      checkScrollLoad();
    }, 400);
    return;
  }

  void fetchLikeCounts(cats.map(c => c.url)).then(() => {
    if (stale(gen)) return;
    for (const c of cats) {
      updateMetaLike(c.url);
    }
  });

  scrollCooldown = true;
  window.setTimeout(() => {
    scrollCooldown = false;
    checkScrollLoad();
  }, 400);
}

export async function loadMore(gen: number = galleryEpoch): Promise<CatItem[] | null | undefined> {
  const filter = state.filter;
  if (filter === 'liked') {
    return loadLiked(gen);
  }
  if (stale(gen, filter) || state.loading || state.ended) return;

  state.loading = true;
  els.loader.style.display = 'block';
  els.loader.textContent = 'ещё котики…';
  const offset = state.offset;
  try {
    const { batch, rawCount, limit } = await fetchMoreGallery(undefined, filter, offset);
    if (stale(gen, filter)) return;
    state.offset += rawCount;
    if (rawCount === 0) {
      state.ended = true;
      return;
    }
    if (rawCount < limit) {
      state.ended = true;
    }
    if (!batch.length) {
      return;
    }
    return batch;
  } catch (e) {
    if (stale(gen, filter)) return;
    els.loader.textContent = 'ошибка подгрузки, попробуй ещё раз';
    console.error(e);
    return null;
  } finally {
    if (!stale(gen, filter)) {
      state.loading = false;
      if (!state.ended) els.loader.style.display = 'none';
    }
  }
}

async function loadLiked(gen: number): Promise<CatItem[] | null | undefined> {
  if (stale(gen, 'liked') || state.loading || state.ended) return;

  state.loading = true;
  els.loader.style.display = 'block';
  els.loader.textContent = 'мои лайки…';
  const offset = state.offset;
  try {
    const { batch, rawCount, limit } = await fetchMyLikesOnly(undefined, offset);
    if (stale(gen, 'liked')) return;
    state.offset += rawCount;
    if (rawCount === 0) {
      state.ended = true;
      if (offset === 0) {
        els.loader.textContent = 'пока нет лайков — жми ♥ в галерее';
      }
      return;
    }
    if (rawCount < limit) {
      state.ended = true;
    }
    els.loader.style.display = 'none';
    return batch;
  } catch (e) {
    if (stale(gen, 'liked')) return;
    els.loader.textContent = 'ошибка загрузки лайков';
    console.error(e);
    return null;
  } finally {
    if (!stale(gen, 'liked')) {
      state.loading = false;
    }
  }
}

export async function resetGallery(open: (index: number) => void): Promise<void> {
  const gen = bumpGalleryEpoch();
  const filter = state.filter;
  resetLikeCache();
  scrollCooldown = false;
  state.loading = false;
  state.allCats = [];
  state.nextId = 1;
  state.offset = 0;
  state.ended = false;
  els.grid.innerHTML = '';
  els.count.textContent = '0';
  const batch = await loadMore(gen);
  if (stale(gen, filter)) return;
  if (batch?.length) appendCats(batch, open, gen);
}

export function shuffleGrid(open: (index: number) => void): void {
  if (state.filter === 'liked') return;
  for (let i = state.allCats.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [state.allCats[i], state.allCats[j]] = [state.allCats[j], state.allCats[i]];
  }
  rebuildGrid(open);
}

export function bindScroll(loadFn: (gen: number) => void): void {
  scrollLoadFn = loadFn;
  const obs = new IntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) checkScrollLoad();
  }, { rootMargin: '250px' });
  obs.observe(els.sentinel);
  window.addEventListener('scroll', () => checkScrollLoad(), { passive: true });
}
